import { ApolloClient } from '@apollo/client'
import { StripeIbanElement, Stripe, StripeCardElement, StripeIdealBankElement } from '@stripe/stripe-js'

import User from './User'

export enum PaymentMethodType {
  PAYMENT_METHOD_BANCONTACT = 'PAYMENT_METHOD_BANCONTACT',
  PAYMENT_METHOD_CREDIT_CARD = 'PAYMENT_METHOD_CREDIT_CARD',
  PAYMENT_METHOD_IDEAL = 'PAYMENT_METHOD_IDEAL',
  PAYMENT_METHOD_MULTIBANCO = 'PAYMENT_METHOD_MULTIBANCO',
  PAYMENT_METHOD_PAYPAL = 'PAYMENT_METHOD_PAYPAL',
  PAYMENT_METHOD_SEPA_ONLINE = 'PAYMENT_METHOD_SEPA_ONLINE',
  PAYMENT_METHOD_SOFORT = 'PAYMENT_METHOD_SOFORT',
  PAYMENT_METHOD_EPS = 'PAYMENT_METHOD_EPS',
  PAYMENT_METHOD_GIROPAY = 'PAYMENT_METHOD_GIROPAY',
  PAYMENT_METHOD_TWINT = 'PAYMENT_METHOD_TWINT',
}

// whitelist payment methods that are supported by the client
const supportPaymentMethodTypes = [
  PaymentMethodType.PAYMENT_METHOD_BANCONTACT,
  PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD,
  PaymentMethodType.PAYMENT_METHOD_IDEAL,
  PaymentMethodType.PAYMENT_METHOD_MULTIBANCO,
  PaymentMethodType.PAYMENT_METHOD_PAYPAL,
  PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE,
  PaymentMethodType.PAYMENT_METHOD_SOFORT,
  PaymentMethodType.PAYMENT_METHOD_EPS,
  PaymentMethodType.PAYMENT_METHOD_GIROPAY,
  PaymentMethodType.PAYMENT_METHOD_TWINT,
] as const

export const isSupportedPaymentMethodType = (paymentMethod: { type: PaymentMethodType }) =>
  supportPaymentMethodTypes.includes(paymentMethod.type as (typeof supportPaymentMethodTypes)[number])

export enum PaymentState {
  PAYMENT_STATE_CANCELLED = 'PAYMENT_STATE_CANCELLED',
  PAYMENT_STATE_FAILED = 'PAYMENT_STATE_FAILED',
  PAYMENT_STATE_IN_PROGRESS = 'PAYMENT_STATE_IN_PROGRESS',
  PAYMENT_STATE_ON_HOLD = 'PAYMENT_STATE_ON_HOLD',
  PAYMENT_STATE_OPEN = 'PAYMENT_STATE_OPEN',
  PAYMENT_STATE_SUCCEEDED = 'PAYMENT_STATE_SUCCEEDED',
}

export enum PaymentMethodVerificationMethod {
  PAYMENT_METHOD_IDEAL = 'PAYMENT_METHOD_IDEAL',
  PAYMENT_METHOD_SOFORT = 'PAYMENT_METHOD_SOFORT',
}

export enum CustomPaymentMethodType {
  PAYMENT_METHOD_OFFLINE = 'PAYMENT_METHOD_OFFLINE',
}

export type AdditionalPaymentMethodType = PaymentMethodType | CustomPaymentMethodType

export const OFFLINE_PAYMENT_METHOD: PaymentMethod = {
  type: CustomPaymentMethodType.PAYMENT_METHOD_OFFLINE,
  name: 'Offline Payment',
}

export const NEW_CARD = 'NEW_CARD'

export interface PaymentMethod {
  type: AdditionalPaymentMethodType
  name: string
}

export interface StoredCreditCard {
  __typename: 'StoredCreditCard'
  id: string
  name: string
  brand: string
  pspId: string
  expirationMonth: number
  expirationYear: number
  last4: string
  preferredNetwork: string | null
}

export interface StoredBankAccount {
  __typename: 'StoredBankAccount'
  id: string
  pspId: string
  last4: string
}
export interface StoredBankAccountWithMandates extends StoredBankAccount {
  mandates: Array<{ isVerified: boolean }>
}

export interface Mandate {
  isVerified: boolean
  bankAccount: StoredBankAccount
}

export type StoredPaymentMethod = StoredCreditCard | StoredBankAccountWithMandates

export interface SelectedPaymentMethod extends CardPaymentMethod, SepaPaymentMethod, SofortPaymentMethod {
  type?: AdditionalPaymentMethodType
}

interface CardPaymentMethod {
  cardId?: string
  saveCard: Array<string>
}

export interface SepaPaymentMethod {
  bankAccountHolder?: string
  pspPaymentMethodId?: string
}

interface SofortPaymentMethod {
  countryIsoCode?: string
}

export type PaymentOption =
  | 'pay-with-ideal'
  | 'pay-with-sepa-ideal'
  | 'pay-with-credit-card'
  | 'additional-payment-methods'

export interface PaymentProvider {
  stripe: Stripe | null
}

export interface PaymentAPI {
  // eslint-disable-next-line @typescript-eslint/ban-types
  apolloClient: ApolloClient<object>
}

export interface PaymentIntent {
  id: string
  clientSecret: string
  amount?: number
  currency?: string
}

export interface PollPaymentStateOptions {
  orderId: string
  lastPaymentAttempt: Date | null
  additionalExpectedPaymentStates?: Array<PaymentState>
}

export interface ConfirmPaymentOptions {
  paymentId: string
  pspPaymentMethodId: string
}

export interface PreparePaymentOptions {
  paymentId: string
  paymentMethodType: PaymentMethodType
}

export interface SetupRecurringPaymentMethodOptions {
  paymentId: string
}

export interface PrepareAndPollPaymentOptions {
  orderId: string
  selectedPaymentMethod: SelectedPaymentMethod
  lastPaymentAttempt: Date
  options: ProcessPaymentOptions
}

export interface ConfirmPaymentAndPollOptions {
  orderId: string
  options: ProcessPaymentOptions
  lastPaymentAttempt: Date
}

export interface SetupRecurringPaymentMethodAndPollOptions {
  orderId: string
  options: ProcessPaymentOptions
  lastPaymentAttempt: Date
}

export interface Payment {
  id: string
  state: PaymentState
  paymentIntent: PaymentIntent | null
  lastAttemptAt: string | null
  canSetupRecurringPaymentMethod: boolean
}

export interface ProcessPaymentOptions {
  payment: Payment
  selectedPaymentMethod: SelectedPaymentMethod
  orderId: string
  user: User
  cardElement: StripeCardElement | null
  ibanElement: StripeIbanElement | null
  idealBankElement: StripeIdealBankElement | null
  verifyWith: PaymentMethodVerificationMethod | null
  isSetupRecurringPaymentMethodApiEnabled: boolean
}

export interface PaymentResponse {
  wasSuccessful: boolean
  error?: string
}

export const PaymentMethodStripeSourceTypeMapping: { [key in PaymentMethodType]: string } = {
  [PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD]: 'card',
  [PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE]: 'sepa_debit',
  [PaymentMethodType.PAYMENT_METHOD_MULTIBANCO]: 'multibanco',
  [PaymentMethodType.PAYMENT_METHOD_BANCONTACT]: 'bancontact',
  [PaymentMethodType.PAYMENT_METHOD_IDEAL]: 'ideal',
  [PaymentMethodType.PAYMENT_METHOD_PAYPAL]: 'paypal',
  [PaymentMethodType.PAYMENT_METHOD_SOFORT]: 'sofort',
  [PaymentMethodType.PAYMENT_METHOD_EPS]: 'eps',
  [PaymentMethodType.PAYMENT_METHOD_GIROPAY]: 'giropay',
  [PaymentMethodType.PAYMENT_METHOD_TWINT]: 'twint',
}

export enum PaymentError {
  PAYMENT_ERROR_REQUEST_TIMEOUT = 'PAYMENT_ERROR_REQUEST_TIMEOUT',
  PAYMENT_ERROR_CANCELLED = 'PAYMENT_ERROR_CANCELLED',
  PAYMENT_ERROR_FAILED = 'PAYMENT_ERROR_FAILED',
  PAYMENT_ERROR_ORDER_ABORTED = 'PAYMENT_ERROR_ORDER_ABORTED',
  PAYMENT_ERROR_STRIPE_SOURCE = 'PAYMENT_ERROR_STRIPE_SOURCE',
}

export const isStoredCreditCard = (storedPaymentMethod: StoredPaymentMethod): storedPaymentMethod is StoredCreditCard =>
  storedPaymentMethod.__typename === 'StoredCreditCard'

export const isStoredBankAccount = (
  storedPaymentMethod: StoredPaymentMethod,
): storedPaymentMethod is StoredBankAccountWithMandates => storedPaymentMethod.__typename === 'StoredBankAccount'
